'use client';

import { useMemo, useRef, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { Box, VStack } from '@terra/ui-styled-system';
import {
  CvssCalculator,
  CvssMetricReasonsFields,
  ObjectEditField,
  ObjectEditFieldGroup,
  ObjectEditSection,
  type CvssMetricReasonsFieldsProps,
  type EditableFields,
} from '@terra/ui-design-system';
import type { CvssEnvironmentalRequirements } from '@terra/shared-cvss';
import { FindingCopilotNote } from '../object-context/FindingCopilotNote';
import { useCopilotReviewArrival } from '../../hooks/useCopilotReviewArrival';
import { formatFindingSuggestionValue } from '../../utils/findingSuggestionFieldUtils';
import {
  getCvssMetricChanges,
  getCvssScoreTransition,
  hasCvssVectorChanged,
  resolveCvssSuggestionBefore,
} from './findingEditCvssSection.utils';
import type { FindingEditMarkableFieldProps } from './findingEditForm.types';

export type FindingEditCvssSectionProps = {
  editableFields: EditableFields;
  cvssCalculatorVersion: '3.1' | '4.0';
  findingCvssVersion: string | null;
  applicationCvssEnvironmentalRequirements?: Partial<CvssEnvironmentalRequirements> | null;
  cvssMarkableFieldProps?: FindingEditMarkableFieldProps;
  severityMarkableFieldProps?: FindingEditMarkableFieldProps;
  onScoreChange: (score: string, vector: string, severity?: string) => void;
  originalCvss: EditableFields['cvss'];
  onReasonsChange: CvssMetricReasonsFieldsProps['onReasonsChange'];
};

export function FindingEditCvssSection({
  editableFields,
  cvssCalculatorVersion,
  findingCvssVersion,
  applicationCvssEnvironmentalRequirements,
  cvssMarkableFieldProps,
  severityMarkableFieldProps,
  onScoreChange,
  originalCvss,
  onReasonsChange,
}: FindingEditCvssSectionProps) {
  const tVersions = useTranslations('designSystem.findings.cvssVersions');
  const tReview = useTranslations(
    'designSystem.findings.editDrawer.suggestionReview',
  );
  const fieldRef = useRef<HTMLDivElement>(null);
  const severityFieldRef = useRef<HTMLDivElement>(null);
  const isAcceptedFlash = Boolean(cvssMarkableFieldProps?.isAcceptedFlash);
  const isReviewing = Boolean(
    cvssMarkableFieldProps?.isReviewing && !isAcceptedFlash,
  );
  const { washPortal } = useCopilotReviewArrival({
    isReviewing,
    reviewArrivalAt: isReviewing
      ? cvssMarkableFieldProps?.reviewArrivalAt
      : undefined,
    elementRef: fieldRef,
  });
  const severityIsAcceptedFlash = Boolean(
    severityMarkableFieldProps?.isAcceptedFlash,
  );
  const severityIsReviewing = Boolean(
    severityMarkableFieldProps?.isReviewing && !severityIsAcceptedFlash,
  );
  const { washPortal: severityWashPortal } = useCopilotReviewArrival({
    isReviewing: severityIsReviewing && !isReviewing,
    reviewArrivalAt:
      severityIsReviewing && !isReviewing
        ? severityMarkableFieldProps?.reviewArrivalAt
        : undefined,
    elementRef: severityFieldRef,
  });
  const resolvedFindingCvssVersion =
    findingCvssVersion === '3.1' || findingCvssVersion === '4.0'
      ? findingCvssVersion
      : null;
  const pendingEdit = cvssMarkableFieldProps?.pendingEdit;
  const hasPendingCvss = Boolean(
    pendingEdit &&
      cvssMarkableFieldProps?.onAcceptPendingEdit &&
      cvssMarkableFieldProps?.onDiscardPendingEdit,
  );
  const severityPendingEdit = severityMarkableFieldProps?.pendingEdit;
  const hasPendingSeverityOnly = Boolean(
    !hasPendingCvss &&
      severityPendingEdit &&
      severityMarkableFieldProps?.onAcceptPendingEdit &&
      severityMarkableFieldProps?.onDiscardPendingEdit,
  );
  const formatSeverityValue =
    severityMarkableFieldProps?.formatPendingValue ??
    ((value: unknown) => formatFindingSuggestionValue({ value }));
  const suggestionSides = useMemo(() => {
    if (!pendingEdit) {
      return null;
    }

    return {
      before: resolveCvssSuggestionBefore({
        pendingBefore: pendingEdit.before,
        pendingAfter: pendingEdit.after,
        original: cvssMarkableFieldProps?.baselineValue,
      }),
      after: pendingEdit.after,
    };
  }, [cvssMarkableFieldProps?.baselineValue, pendingEdit]);
  const metricChanges = useMemo(
    () =>
      suggestionSides
        ? getCvssMetricChanges({
            before: suggestionSides.before,
            after: suggestionSides.after,
            version: cvssCalculatorVersion,
          })
        : [],
    [cvssCalculatorVersion, suggestionSides],
  );
  const metricFooters = useMemo(() => {
    if (!hasPendingCvss || metricChanges.length === 0) {
      return undefined;
    }

    return metricChanges.reduce<Record<string, ReactNode>>(
      (footers, change) => {
        footers[change.field] = (
          <FindingCopilotNote
            variant="affected-transition"
            beforeLabel={change.beforeLabel}
            afterLabel={change.afterLabel}
          />
        );

        return footers;
      },
      {},
    );
  }, [hasPendingCvss, metricChanges]);
  const scoreTransition = suggestionSides
    ? getCvssScoreTransition({
        before: suggestionSides.before,
        after: suggestionSides.after,
      })
    : null;
  const vectorChanged =
    suggestionSides &&
    hasCvssVectorChanged({
      before: suggestionSides.before,
      after: suggestionSides.after,
    });
  const changedMetricCount = Math.max(
    metricChanges.length,
    vectorChanged ? 1 : 0,
  );
  const copilotChrome = hasPendingCvss
    ? {
        groupAlert: (
          <FindingCopilotNote
            variant="group-message"
            message={tReview('baseMetricsChanged', {
              count: changedMetricCount > 0 ? changedMetricCount : 1,
            })}
            isFocused={isReviewing}
            onAccept={cvssMarkableFieldProps?.onAcceptPendingEdit}
            onDiscard={cvssMarkableFieldProps?.onDiscardPendingEdit}
          />
        ),
        vectorFooter: vectorChanged ? (
          <FindingCopilotNote
            variant="affected-label"
            label={tReview('recalculated')}
          />
        ) : undefined,
        baseScoreFooter: scoreTransition ? (
          <FindingCopilotNote
            variant="affected-transition"
            beforeLabel={scoreTransition.beforeLabel}
            afterLabel={scoreTransition.afterLabel}
          />
        ) : undefined,
        metricFooters,
        isReviewing,
      }
    : isAcceptedFlash
      ? {
          groupAlert: <FindingCopilotNote variant="accepted" />,
        }
      : undefined;
  const severitySuggestionChrome = hasPendingSeverityOnly ? (
    <FindingCopilotNote
      variant="suggested-select"
      beforeLabel={
        formatSeverityValue(severityPendingEdit?.before) ||
        tReview('emptyValue')
      }
      afterLabel={
        formatSeverityValue(severityPendingEdit?.after) || tReview('emptyValue')
      }
      isFocused={severityIsReviewing}
      onAccept={severityMarkableFieldProps?.onAcceptPendingEdit}
      onDiscard={severityMarkableFieldProps?.onDiscardPendingEdit}
    />
  ) : severityIsAcceptedFlash && !isAcceptedFlash ? (
    <FindingCopilotNote variant="accepted" />
  ) : null;

  return (
    <ObjectEditSection
      title={tVersions('editorTitle', { version: cvssCalculatorVersion })}
    >
      <ObjectEditFieldGroup>
        <ObjectEditField span="full">
          <VStack gap={3} alignItems="stretch" w="full">
            <Box
              ref={fieldRef}
              data-field-anchor="cvss"
              data-pending-review-field={hasPendingCvss ? 'cvss' : undefined}
              w="full"
              borderRadius="md"
            >
              {washPortal}
              <CvssCalculator
                key={cvssCalculatorVersion}
                initialScore={editableFields.cvss?.score?.toString() || ''}
                initialVector={editableFields.cvss?.vector || ''}
                initialEnvironmentalVector={
                  editableFields.cvss?.environmentalVector
                }
                version={cvssCalculatorVersion}
                findingVersion={resolvedFindingCvssVersion}
                environmentalRequirements={
                  applicationCvssEnvironmentalRequirements
                }
                copilotChrome={copilotChrome}
                onScoreChange={onScoreChange}
              />
            </Box>
            <CvssMetricReasonsFields
              originalCvss={originalCvss}
              cvss={editableFields.cvss}
              onReasonsChange={onReasonsChange}
            />
            {severitySuggestionChrome ? (
              <Box
                ref={severityFieldRef}
                data-field-anchor="severity"
                data-pending-review-field={
                  hasPendingSeverityOnly ? 'severity' : undefined
                }
                w="full"
                borderRadius="md"
                borderWidth="1px"
                borderStyle="solid"
                borderColor={
                  severityIsReviewing ? 'border.accent.default' : 'transparent'
                }
                transition="border-color 220ms ease"
              >
                {severityWashPortal}
                {severitySuggestionChrome}
              </Box>
            ) : null}
          </VStack>
        </ObjectEditField>
      </ObjectEditFieldGroup>
    </ObjectEditSection>
  );
}
